import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getAdminPageMeta, type AdminPageMeta } from "@/lib/adminPageCatalog";

type AdminPageHeaderProps = {
  title?: string;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /**
   * Catalog id path override (default: useLocation().pathname).
   * When catalog matches, shows purpose / owns / notHere + related chips.
   */
  catalogPath?: string;
  /** Hide auto related links from catalog. */
  hideRelated?: boolean;
  /** Extra related links beyond catalog. */
  extraRelated?: Array<{ path: string; label: string }>;
};

export function AdminPageHeader({
  title,
  description,
  actions,
  className,
  catalogPath,
  hideRelated,
  extraRelated,
}: AdminPageHeaderProps) {
  const { pathname } = useLocation();
  const meta: AdminPageMeta | null = getAdminPageMeta(catalogPath || pathname);

  const heading = title || meta?.title || "Admin";
  const purpose = meta?.purpose;
  const owns = meta?.owns;
  const notHere = meta?.notHere;
  const related = [
    ...(hideRelated ? [] : meta?.related ?? []),
    ...(extraRelated ?? []),
  ];

  return (
    <header
      className={cn(
        "mb-6 flex flex-col gap-4 rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.05] to-white/[0.02] px-5 py-5 shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] backdrop-blur-sm",
        className,
      )}
      data-admin-surface="page-header"
    >
      {/*
        Stack by default. Only go side-by-side on wide screens so dense action
        toolbars never crush the title into single-character wrapping.
      */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1 space-y-2 xl:max-w-[54%]">
          <h1 className="text-balance text-xl font-semibold tracking-tight text-white sm:text-2xl">
            {heading}
          </h1>
          {description ? (
            <div className="max-w-3xl text-pretty text-sm leading-relaxed text-white/50 [&_code]:rounded-lg [&_code]:border [&_code]:border-white/[0.08] [&_code]:bg-white/[0.04] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-xs [&_code]:text-white/70 [&_strong]:font-semibold [&_strong]:text-white/75">
              {description}
            </div>
          ) : purpose ? (
            <p className="max-w-3xl text-pretty text-sm leading-relaxed text-white/50">
              {purpose}
            </p>
          ) : null}

          {(owns || notHere) && (
            <div className="mt-1 max-w-3xl space-y-1 text-[11px] leading-relaxed">
              {owns && (
                <p className="text-white/45">
                  <span className="font-semibold text-white/60">Editar acá:</span>{" "}
                  {owns}
                </p>
              )}
              {notHere && (
                <p className="text-white/35">
                  <span className="font-semibold text-white/50">No es acá:</span>{" "}
                  {notHere}
                </p>
              )}
            </div>
          )}
        </div>
        {actions ? (
          <div className="flex w-full flex-wrap items-center gap-2 xl:w-auto xl:max-w-[46%] xl:shrink-0 xl:justify-end">
            {actions}
          </div>
        ) : null}
      </div>

      {related.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-white/[0.06] pt-3">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-white/30">
            Relacionado
          </span>
          {related.map((r) => (
            <Link
              key={r.path}
              to={r.path}
              className="rounded-full border border-white/[0.09] bg-white/[0.04] px-2.5 py-1 text-[11px] text-white/55 transition-colors hover:border-white/18 hover:bg-white/[0.08] hover:text-white/85"
            >
              {r.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
